import { OrderedSet, List } from 'immutable';
import { Mark, Block } from 'slate';
import { STYLE_TYPES, TEXT_ALIGN, TEXT_DECORATION, TEXT_SCRIPT, TEXT_TRANSFORM } from './constants';
import { colorMarkClassName, subScriptClassName, superScriptClassName } from '../../../../containers/Editor/constants';

export interface MarkProperties {
	type: STYLE_TYPES;
	data: {
		style: { [key: string]: string },
		className?: string;
	};
}

export const getMarksByType = (marks: OrderedSet<Mark>, type: STYLE_TYPES) => (
	marks.filter((mark: Mark) => mark.type === type)
);

const getMark = (type: STYLE_TYPES, style: {}, className?: string): MarkProperties => ({
	type,
	data: { style, className },
});

export const getFontFamily = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, STYLE_TYPES.FONT_FAMILY);

	if (!marks.size) {
		return '';
	}

	const { fontFamily } = marks.first().data.get('style');
	return fontFamily;
};

export const getFontSize = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, STYLE_TYPES.FONT_SIZE);

	if (!marks.size) {
		return '';
	}

	const { fontSize } = marks.first().data.get('style');
	return fontSize;
};

export const getLetterSpacing = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, STYLE_TYPES.LETTER_SPACING);

	if (!marks.size) {
		return '0px';
	}

	const { letterSpacing } = marks.first().data.get('style');
	return letterSpacing;
};

export const getColor = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, STYLE_TYPES.COLOR);

	if (!marks.size) {
		return '';
	}

	const { color } = marks.first().data.get('style');
	return color;
};

export const getLineHeight = (blocks: List<Block>): string => {
	if (!blocks.size) {
		return '';
	}

	const style = blocks.first().data.get('style');

	if (!style) {
		return '';
	}

	return style.lineHeight;
};

export const isFontWeightBold = (marks: OrderedSet<Mark>): boolean => {
	const filteredMarks = getMarksByType(marks, STYLE_TYPES.FONT_WEIGHT);
	return filteredMarks.size > 0 && filteredMarks.every((mark: Mark) => {
		const { fontWeight } = mark.data.get('style');
		return fontWeight === 'bold' || fontWeight >= 600;
	});
};

export const isFontStyleItalic = (marks: OrderedSet<Mark>): boolean => {
	const filteredMarks = getMarksByType(marks, STYLE_TYPES.FONT_STYLE);
	return filteredMarks.size > 0 && filteredMarks.every((mark: Mark) => (
		mark.data.get('style').fontStyle === 'italic'
	));
};

export const isTextDecorationUnderline = (marks: OrderedSet<Mark>): boolean => {
	const filteredMarks = getMarksByType(marks, STYLE_TYPES.TEXT_DECORATION);
	return filteredMarks.size > 0 && filteredMarks.every((mark: Mark) => (
		mark.data.get('style').textDecoration === TEXT_DECORATION.UNDERLINE
	));
};

export const isTextDecorationLineThrough = (marks: OrderedSet<Mark>): boolean => {
	const filteredMarks = getMarksByType(marks, STYLE_TYPES.TEXT_DECORATION);
	return filteredMarks.size > 0 && filteredMarks.every((mark: Mark) => (
		mark.data.get('style').textDecoration === TEXT_DECORATION.LINE_THROUGH
	));
};

export const isTextTransformCapitalize = (marks: OrderedSet<Mark>): boolean => {
	const filteredMarks = getMarksByType(marks, STYLE_TYPES.TEXT_TRANSFORM);
	return filteredMarks.size > 0 && filteredMarks.every((mark: Mark) => (
		mark.data.get('style').textTransform === TEXT_TRANSFORM.CAPITALIZE
	));
};

export const isTextTransformUpperCase = (marks: OrderedSet<Mark>): boolean => {
	const filteredMarks = getMarksByType(marks, STYLE_TYPES.TEXT_TRANSFORM);
	return filteredMarks.size > 0 && filteredMarks.every((mark: Mark) => (
		mark.data.get('style').textTransform === TEXT_TRANSFORM.UPPERCASE
	));
};

export const isTextTransformLowerCase = (marks: OrderedSet<Mark>): boolean => {
	const filteredMarks = getMarksByType(marks, STYLE_TYPES.TEXT_TRANSFORM);
	return filteredMarks.size > 0 && filteredMarks.every((mark: Mark) => (
		mark.data.get('style').textTransform === TEXT_TRANSFORM.LOWERCASE
	));
};

export const isSuperScript = (marks: OrderedSet<Mark>): boolean => {
	const filteredMarks = getMarksByType(marks, STYLE_TYPES.TEXT_SCRIPT);
	return filteredMarks.size > 0 && filteredMarks.every((mark: Mark) => (
		mark.data.get('className') === superScriptClassName
	));
};

export const isSubScript = (marks: OrderedSet<Mark>): boolean => {
	const filteredMarks = getMarksByType(marks, STYLE_TYPES.TEXT_SCRIPT);
	return filteredMarks.size > 0 && filteredMarks.every((mark: Mark) => (
		mark.data.get('className') === subScriptClassName
	));
};

const getTextDecoration = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, STYLE_TYPES.TEXT_DECORATION);

	if (!marks.size) {
		return TEXT_DECORATION.NONE;
	}

	const { textDecoration } = marks.first().data.get('style');
	return textDecoration;
};

const getTextTransform = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, STYLE_TYPES.TEXT_TRANSFORM);

	if (!marks.size) {
		return TEXT_TRANSFORM.NONE;
	}

	const { textTransform } = marks.first().data.get('style');
	return textTransform;
};

const getTextScriptClassName = (allMarks: OrderedSet<Mark>): string => {
	const marks = getMarksByType(allMarks, STYLE_TYPES.TEXT_SCRIPT);

	if (!marks.size) {
		return '';
	}

	const className = marks.first().data.get('className');
	return className;
};

const checkTextAlign = (blocks: List<Block>, textAlign: string): boolean => {
	if (!blocks.size) {
		return false;
	}

	return blocks.every((block: Block) => {
		const style = block.data.get('style');
		return !!style && style.textAlign === textAlign;
	});
};

export const isTextAlignLeft = (blocks: List<Block>): boolean => checkTextAlign(blocks, TEXT_ALIGN.LEFT);
export const isTextAlignCenter = (blocks: List<Block>): boolean => checkTextAlign(blocks, TEXT_ALIGN.CENTER);
export const isTextAlignRight = (blocks: List<Block>): boolean => checkTextAlign(blocks, TEXT_ALIGN.RIGHT);
export const isTextAlignJustify = (blocks: List<Block>): boolean => checkTextAlign(blocks, TEXT_ALIGN.JUSTIFY);

export const getFontFamilyMark = (fontFamily: string): MarkProperties => (
	getMark(STYLE_TYPES.FONT_FAMILY, { fontFamily })
);

export const getFontSizeMark = (fontSize: string): MarkProperties => (
	getMark(STYLE_TYPES.FONT_SIZE, { fontSize })
);

export const getColorMark = (color: string): MarkProperties => (
	getMark(STYLE_TYPES.COLOR, { color }, colorMarkClassName)
);

export const getFontWeightMark = (marks: OrderedSet<Mark>): MarkProperties => {
	const fontWeight = isFontWeightBold(marks) ? 'normal' : 'bold';
	return getMark(STYLE_TYPES.FONT_WEIGHT, { fontWeight });
};

export const getFontStyleMark = (marks: OrderedSet<Mark>): MarkProperties => {
	const fontStyle = isFontStyleItalic(marks) ? 'normal' : 'italic';
	return getMark(STYLE_TYPES.FONT_STYLE, { fontStyle });
};

export const getTextDecorationMark = (marks: OrderedSet<Mark>, value: string): MarkProperties => {
	const currentTextDecoration = getTextDecoration(marks);
	const newTextDecoration = currentTextDecoration === value
		? TEXT_DECORATION.NONE
		: value;

	return getMark(STYLE_TYPES.TEXT_DECORATION, { textDecoration: newTextDecoration });
};

export const getTextTransformMark = (marks: OrderedSet<Mark>, value: string): MarkProperties => {
	const currentTextTransform = getTextTransform(marks);
	const newTextTransform = currentTextTransform === value
		? TEXT_TRANSFORM.NONE
		: value;

	return getMark(STYLE_TYPES.TEXT_TRANSFORM, { textTransform: newTextTransform });
};

export const getTextScriptMark = (marks: OrderedSet<Mark>, value: string): MarkProperties => {
	const currentClassName = getTextScriptClassName(marks);
	const className = value === TEXT_SCRIPT.SUB ? subScriptClassName : superScriptClassName;
	const newClassName = currentClassName === className
		? ''
		: className;

	return getMark(STYLE_TYPES.TEXT_SCRIPT, {}, newClassName);
};

export const getLetterSpacingMark = (letterSpacing: string): MarkProperties => (
	getMark(STYLE_TYPES.LETTER_SPACING, { letterSpacing })
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/RichTextEditor/helpers/marks.ts